import { useEffect, useState } from 'react';
import { api, post, isErr, type AskResult, type AskStatus, type Entity } from '../lib/api.ts';

/* Ask.tsx — the natural-language bar over the grid, and the only control in this product that
 * spends money.
 *
 * ══ THE CONSTRAINT IT IS SHAPED BY ══
 * A plain company-name lookup must be free and must not route through a model. In the previous
 * build a name lookup went through the model at roughly $0.17 a time.
 *
 * The rail filter builder, the column picker and the Atlas search are therefore compiled filters
 * with no code path to a model. This control is separate, never automatic, states its own price
 * and the remaining hourly budget before it is pressed, and reports the actual cost after. Spend is
 * visible at the moment it is incurred rather than in a later bill.
 *
 * ══ THE ANSWER IS EDITABLE, NOT AUTHORITATIVE ══
 * The result lands as an ordinary filter AST in the grid's existing `raw` chip, the same mechanism
 * the gap ledger hands over through, so a partially correct answer is adjusted with the free
 * controls rather than re-asked at further cost. `ignored` and `dropped` are rendered as
 * prominently as the answer: a filter that silently discards part of the question produces a count
 * that reads as authoritative and is not.
 *
 * ══ DELIBERATELY NOT PORTED ══
 * The previous build ran a ~500-line offline parser first and escalated to the model only on
 * leftover words. It existed because that grid had no usable filter builder. This one does, plus a
 * searchable picker over all 116 columns, so rebuilding the parser would add a second filter
 * grammar to keep in step with the registry. Recorded as a deliberate divergence.
 */

export function Ask({ onResult }: {
  onResult: (r: { ent: Entity; q: unknown; label: string; sort: string | null; dir: 'asc' | 'desc' }) => void;
}) {
  const [text, setText] = useState('');
  const [status, setStatus] = useState<AskStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<AskResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Asked once on mount. Every answer carries a fresh status, so this is the only poll.
  useEffect(() => {
    api<AskStatus>('askai/status').then((s) => setStatus(isErr(s) ? null : s));
  }, []);

  const ask = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true); setErr(null); setOut(null);
    const r = await post<AskResult>(`askai?q=${encodeURIComponent(q)}`);
    setBusy(false);

    if (isErr(r)) { setErr(r.error); return; }
    setStatus(r.status);
    setOut(r);

    /* An answer with no conditions is not applied. Filtering to everything and calling it a result
       reads as success; saying nothing matched is the truth. */
    if (!r.ast.kids.length && !r.sort) return;
    onResult({
      ent: r.entity,
      q: r.ast.kids.length ? r.ast : null,
      label: r.read || q,
      sort: r.sort,
      dir: r.dir,
    });
  };

  const disabled = busy || !text.trim() || !(status?.enabled ?? false);

  return (
    <div style={{ borderBottom: 'var(--hair)', padding: '8px 12px' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
          placeholder="Ask a question in plain words — e.g. aerospace companies in Texas with more than 3 plants"
          maxLength={400}
          style={{
            flex: 1, padding: '6px 8px', border: 'var(--hair)', background: 'transparent',
            color: '#FFF', fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
          }}
        />
        <button
          onClick={ask}
          disabled={disabled}
          title={status?.enabled
            ? `Sends ONLY your question and your own vertical and action names to Claude. No row, no contact and no count ever leaves this machine.\n\nLeft this hour: $${status.remainingUsd} of $${status.capUsd}\nLeft today: $${status.remainingDayUsd} of $${status.capDayUsd}\nQuestions asked: ${status.callsHour} this hour, ${status.callsDay} today`
            : (status?.reason || 'AI reading unavailable')}
          style={{
            padding: '6px 12px', cursor: disabled ? 'not-allowed' : 'pointer',
            border: 'var(--hair)', background: disabled ? 'transparent' : 'rgba(255,170,0,.043)',
            color: disabled ? 'var(--dim)' : '#FFF',
            fontSize: 'var(--t-body)', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
          }}
        >{busy ? 'asking Claude…' : 'Ask Claude →'}</button>
      </div>

      {/* The honest status line. Four states — off, no key, a cap biting, or ready with a number —
          and it always says which. "AI unavailable" with no reason is how a switched-off feature
          reads as a broken one. */}
      <div style={{ marginTop: 5, fontSize: 'var(--t-micro)', color: 'var(--dim)', lineHeight: 1.5 }}>
        {status == null
          ? 'checking the AI budget…'
          : status.enabled
            ? <>This is the only control here that costs money — the filters and the search are free. ${status.remainingUsd} left this hour of ${status.capUsd}.</>
            : <span style={{ color: status.capped ? 'var(--or)' : 'var(--dim)' }}>{status.reason}</span>}
      </div>

      {err && <div style={{ marginTop: 5, fontSize: 'var(--t-micro)', color: 'var(--or)' }}>{err}</div>}

      {out && (
        <div style={{ marginTop: 6, fontSize: 'var(--t-micro)', lineHeight: 1.6 }}>
          <span style={{ color: 'var(--dim)' }}>read as: </span>
          <span style={{ color: '#FFF' }}>{out.read || '(nothing)'}</span>
          {!out.ast.kids.length && !out.sort && (
            <span style={{ color: 'var(--or)' }}> · nothing in that matched a column, a tag or a value from your files, so the grid was left alone</span>
          )}
          {out.ignored && <span style={{ color: 'var(--or)' }}> · ignored: “{out.ignored}”</span>}
          {out.dropped.length > 0 && (
            <span style={{ color: 'var(--or)' }}> · dropped as not real: {out.dropped.join('; ')}</span>
          )}
          {out.sort && <span style={{ color: 'var(--dim)' }}> · ranked by {out.sort} {out.dir === 'asc' ? '↑' : '↓'}</span>}
          <span style={{ color: 'var(--dim)' }}> · read by Claude ({out.model}, ${out.costUsd.toFixed(3)})</span>
        </div>
      )}
    </div>
  );
}
